import Singleton from "../Singleton";

import { ACTION, DATA_ORIGIN, PRODUCT, RESOURCE } from "./enums";
import { UserApiClientError } from "./Error";
import {
  GetUserAuthorsQuery,
  GetUserTopicsQuery,
  GetUserEngagementsQuery,
  GetUserSecuritiesQuery,
  FollowMutation,
  DeleteFollowMutation,
} from "./queries";
import { UserEngagement, UserEngagementsResponse, TopicParams } from "./types";

export type UserApiClientParams = {
  host: string;
};

export * from "./types";

export default class UserApiClient extends Singleton {
  host: string;

  constructor(params: UserApiClientParams) {
    super();

    if (!params.host) {
      throw new Error(`No host passed to UserApiClient constructor; aborting`);
    }

    this.host = `${params.host}/graphql`;
  }

  async getUserEngagements(): Promise<UserEngagement[]> {
    const payload = {
      operationName: "getUser",
      query: GetUserEngagementsQuery,
    };

    const options = this.#getRequestInit(payload);

    const response = await fetch(this.host, options);

    if (!response.ok) {
      throw new UserApiClientError({
        errorResponse: [{ message: response.statusText }],
        status: response.status,
        url: this.host,
      });
    }

    const json: UserEngagementsResponse = await response.json();

    if (json.errors) {
      throw new UserApiClientError({
        errorResponse: json.errors,
        status: response.status,
        url: this.host,
      });
    }

    return json.data.user.engagements.elements;
  }

  async getUserAuthors(): Promise<UserEngagement[]> {
    const payload = {
      operationName: "getUserAuthors",
      query: GetUserAuthorsQuery,
    };

    const options = this.#getRequestInit(payload);

    const response = await fetch(this.host, options);

    if (!response.ok) {
      throw new UserApiClientError({
        errorResponse: [{ message: await response.statusText }],
        status: response.status,
        url: this.host,
      });
    }

    const json: UserEngagementsResponse = await response.json();

    if (json.errors) {
      throw new UserApiClientError({
        errorResponse: json.errors,
        status: response.status,
        url: this.host,
      });
    }

    return json.data.user.engagements.elements;
  }

  async getUserTopics(): Promise<UserEngagement[]> {
    const payload = {
      operationName: "getUserTopics",
      query: GetUserTopicsQuery,
    };

    const options = this.#getRequestInit(payload);

    const response = await fetch(this.host, options);

    if (!response.ok) {
      throw new UserApiClientError({
        errorResponse: [{ message: response.statusText }],
        status: response.status,
        url: this.host,
      });
    }

    const json: UserEngagementsResponse = await response.json();

    if (json.errors) {
      throw new UserApiClientError({
        errorResponse: json.errors,
        status: response.status,
        url: this.host,
      });
    }

    return json.data.user.engagements.elements;
  }

  async getUserSecurities(): Promise<UserEngagement[]> {
    const payload = {
      operationName: "getUserSecurities",
      query: GetUserSecuritiesQuery,
    };

    const options = this.#getRequestInit(payload);

    const response = await fetch(this.host, options);

    if (!response.ok) {
      throw new UserApiClientError({
        errorResponse: [{ message: response.statusText }],
        status: response.status,
        url: this.host,
      });
    }

    const json: UserEngagementsResponse = await response.json();

    if (json.errors) {
      throw new UserApiClientError({
        errorResponse: json.errors,
        status: response.status,
        url: this.host,
      });
    }

    return json.data.user.engagements.elements;
  }

  async followTopic(params: TopicParams): Promise<boolean> {
    this.validateTopicParams(params);

    const { topicId } = params;

    const payload = {
      query: FollowMutation,
      variables: {
        user: {},
        engagementsUpdate: {
          creates: [
           {
             dataOrigin: DATA_ORIGIN.HOME_FEED,
             productType: PRODUCT.ENGAGEMENT_PREFERENCES,
             resourceType: RESOURCE.TOPIC,
             resourceId: topicId,
             actionType: ACTION.FOLLOW,
           }
          ],
        },
      },
    };

    const options = this.#getRequestInit(payload);

    const response = await fetch(this.host, options);

    if (!response.ok) {
      throw new UserApiClientError({
        errorResponse: [{ message: response.statusText }],
        status: response.status,
        url: this.host,
      });
    }

    const json = await response.json();

    if (json.errors) {
      throw new UserApiClientError({
        errorResponse: json.errors,
        status: response.status,
        url: this.host,
      });
    }

    return json.data.bulkUpdateEngagements.success;
  }

  async deleteTopic(params: TopicParams): Promise<boolean> {
    this.validateTopicParams(params);

    const { topicId } = params;

    const payload = {
      query: DeleteFollowMutation,
      variables: {
        user: {},
        deleteEngagement: {
          productType: PRODUCT.ENGAGEMENT_PREFERENCES,
          actionType: ACTION.FOLLOW,
          resourceType: RESOURCE.TOPIC,
          resourceId: topicId,
        }
      },
    };

    const options = this.#getRequestInit(payload);

    const response = await fetch(this.host, options);

    if (!response.ok) {
      throw new UserApiClientError({
        errorResponse: [{ message: response.statusText }],
        status: response.status,
        url: this.host,
      });
    }

    const json = await response.json();

    if (json.errors) {
      throw new UserApiClientError({
        errorResponse: json.errors,
        status: response.status,
        url: this.host,
      });
    }

    return json.data.deleteEngagement.success;
  }

  #getRequestInit = (payload: {
    operationName?: string;
    query: string;
    variables?: object;
  }): RequestInit => ({
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
    body: JSON.stringify(payload),
  });

  validateTopicParams = (params: TopicParams): boolean => {
    if (!params.topicId || typeof params.topicId !== "string") {
      throw new Error("Missing or invalid topic parameter for UserApiClient: topicId");
    }
  
    return true;
  }
}
